import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Quote, ArrowLeft, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Flag from "@/components/Flag";
import { useI18n, pick } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { api } from "@/lib/api";

const COUNTRIES = ["Казахстан", "Узбекистан", "Кыргызстан", "Таджикистан", "Туркменистан", "Россия", "Беларусь", "Украина", "Молдова", "Грузия", "Армения", "Азербайджан"];

const initialsAvatar = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "?")}&background=2563EB&color=fff&size=128&bold=true`;

export default function ReviewsPage() {
  const { lang, t } = useI18n();
  const { content, reviews, refetch } = useContent();
  const sec = content?.reviews_section;
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", country: "Казахстан", role: "", rating: 5, text: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);

  const h = sec ? pick(sec, lang, sec.ru) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error(lang === "ru" ? "Заполните имя и текст отзыва" : "Fill name and text");
      return;
    }
    if (!form.phone.trim() || !form.email.trim()) {
      toast.error(lang === "ru" ? "Укажите телефон и email — без них отзыв не рассматривается" : "Phone and email are required");
      return;
    }
    setSending(true);
    try {
      await api.post("/reviews", form);
      setSubmitted(true);
      refetch();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (lang === "ru" ? "Ошибка отправки" : "Send error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="reviews-back">
            <ArrowLeft className="size-4" /> {t("common.back_home")}
          </Link>

          <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-16">
            {/* Left: Reviews list */}
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-4">
                {lang === "ru" ? "Отзывы клиентов" : "Client reviews"}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-semibold tracking-tight mb-4" data-testid="reviews-page-title">
                {h?.title || "Отзывы"}
              </h1>
              <p className="text-base text-muted-foreground mb-12 max-w-2xl">{h?.subtitle}</p>

              {reviews.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground border border-dashed border-border rounded-3xl">
                  {lang === "ru" ? "Пока нет одобренных отзывов." : "No approved reviews yet."}
                </div>
              ) : (
                <div className="space-y-5">
                  {reviews.map((r, i) => (
                    <motion.article
                      key={r.id}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-30px" }}
                      transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
                      className="relative p-5 sm:p-7 lg:p-8 rounded-3xl border border-border bg-card"
                      data-testid={`reviews-page-item-${i}`}
                    >
                      <Quote className="absolute top-6 right-6 size-7 text-accent/25" />
                      <div className="flex flex-wrap items-center gap-4 mb-5">
                        <img
                          src={r.photo || initialsAvatar(r.name)}
                          alt=""
                          className="size-14 rounded-full object-cover ring-2 ring-background"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-semibold text-lg">{r.name}</div>
                          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                            <Flag country={r.country} size={18} />
                            <span>{r.country}{r.role ? ` · ${r.role}` : ""}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }).map((_, k) => (
                            <Star key={k} className={`size-4 ${k < r.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-[15px] leading-relaxed text-foreground/90">"{r.text}"</p>
                      <div className="mt-4 text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Sticky leave-review form */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 p-5 sm:p-7 lg:p-8 rounded-3xl border border-border bg-card" data-testid="leave-review-panel">
                {submitted ? (
                  <div className="py-6 text-center">
                    <div className="size-16 mx-auto mb-4 rounded-full bg-accent/15 flex items-center justify-center">
                      <CheckCircle2 className="size-8 text-accent" />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold mb-2">{h?.form_thanks_title}</h3>
                    <p className="text-sm text-muted-foreground mb-6">{h?.form_thanks_text}</p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: "", country: "Казахстан", role: "", rating: 5, text: "", phone: "", email: "" }); }}
                      className="h-11 px-6 rounded-full bg-foreground text-background font-semibold text-sm"
                      data-testid="leave-another-review-btn"
                    >
                      {lang === "ru" ? "Оставить ещё" : "Leave another"}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" data-testid="leave-review-form">
                    <div>
                      <div className="font-heading text-2xl font-semibold mb-1">{h?.form_title}</div>
                      <div className="text-sm text-muted-foreground mb-2">
                        {lang === "ru" ? "Ваш отзыв пройдёт модерацию." : "Your review will be moderated."}
                      </div>
                      <div className="p-3 rounded-2xl bg-destructive/10 border border-destructive/30 text-xs text-destructive leading-relaxed mb-4" data-testid="contact-warning">
                        <strong className="font-semibold">{lang === "ru" ? "Важно: " : "Important: "}</strong>
                        {t("reviews_form.contact_warning")}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("reviews_form.name")} *</label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full h-11 px-4 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                        data-testid="leave-review-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("reviews_form.country")} *</label>
                      <select
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                        className="w-full h-11 px-3 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                        data-testid="leave-review-country"
                      >
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("reviews_form.role")}</label>
                      <input
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        placeholder={lang === "ru" ? "Например: Складская работа" : "e.g. Warehouse worker"}
                        className="w-full h-11 px-4 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                        data-testid="leave-review-role"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("reviews_form.phone")} *</label>
                        <input
                          required
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="+7 ..."
                          className="w-full h-11 px-4 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          data-testid="leave-review-phone"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("reviews_form.email")} *</label>
                        <input
                          required
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="name@example.com"
                          className="w-full h-11 px-4 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          data-testid="leave-review-email"
                        />
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground -mt-2">
                      {t("reviews_form.contact_private")}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("reviews_form.rating")}</label>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setForm({ ...form, rating: n })}
                            className="p-1"
                            data-testid={`leave-review-rating-${n}`}
                          >
                            <Star className={`size-7 transition-colors ${n <= form.rating ? "fill-accent text-accent" : "text-muted-foreground/40 hover:text-accent/50"}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{t("reviews_form.text")} *</label>
                      <textarea
                        required
                        rows={5}
                        value={form.text}
                        onChange={(e) => setForm({ ...form, text: e.target.value })}
                        placeholder={t("reviews_form.placeholder_text")}
                        className="w-full px-4 py-3 rounded-2xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                        data-testid="leave-review-text"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-foreground text-background font-semibold text-sm disabled:opacity-50"
                      data-testid="leave-review-submit"
                    >
                      <Send className="size-4" />
                      {sending ? "..." : t("reviews_form.submit")}
                    </button>
                  </form>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
